/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

import { useMutation } from '@apollo/client';
import * as Sentry from '@sentry/react';

import config from 'js/app/config';
import { FormattedMessage } from 'js/lib/coursera.react-intl';
import { isAuthenticatedUser } from 'js/lib/user';

import { Button, Dialog, Divider, InlineNotification, Link, TextField, Typography2 } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import InternationalPhoneNumberInput from 'bundles/account-profile/components/i18n-phone-number-input/InternationalPhoneNumberInput';
import type { ProfilePhone } from 'bundles/account-profile/components/types';
import { EMAIL_CONSTRAINT as isEmailValid } from 'bundles/authentication/constants';
import { EoiSSO } from 'bundles/expression-of-interest/components/eoi-optimizations/EoiSSO';
import type {
  EmailAccountAssociationResponse,
  EmailAccountAssociationVariables,
} from 'bundles/expression-of-interest/components/eoi-optimizations/queries/WesCheckoutsV1';
import { EMAIL_ACCOUNT_ASSOCIATION } from 'bundles/expression-of-interest/components/eoi-optimizations/queries/WesCheckoutsV1';
import {
  firstNameErrorCheck,
  fullNameErrorCheck,
  getDcatEoiVariant,
  getDhubEoiVariant,
  getVariantHubEoi,
  isDHubEoiOptEnabled,
  lastNameErrorCheck,
} from 'bundles/expression-of-interest/utils/util';
import type { Phone } from 'bundles/naptimejs/resources/__generated__/ProfilesV1';
import { customFormattedPhoneStyles } from 'bundles/university-program-qualification/components/SurveyQuestion';

import _t from 'i18n!nls/expression-of-interest';

type Props = {
  handleFirstNameChange: (firstName: string) => void;
  handleLastNameChange: (lastName: string) => void;
  handleEmailChange: (email: string) => void;
  handlePhoneChange: (phone: ProfilePhone) => void;
  firstName: string;
  lastName: string;
  learnerEmail: string;
  phone: Phone;
  handleSubmit: () => Promise<void | {}>;
  navigateSignup: () => void;
  navigateConfirmation: () => void;
  navigateSelectSubject: () => void;
  productId: string;
  setEmailAssociatedWithAccount: (isAssociated: boolean) => void;
};

const styles = {
  textField: css`
    margin-bottom: var(--cds-spacing-150);

    label {
      margin: 0;
    }
  `,

  divider: css`
    margin-bottom: var(--cds-spacing-150);
    margin-top: var(--cds-spacing-150);
  `,

  supportText: css`
    margin-top: var(--cds-spacing-150);
  `,
};

export const InitialSection = (props: Props): React.ReactElement => {
  const {
    handleFirstNameChange,
    handleLastNameChange,
    handleEmailChange,
    handlePhoneChange,
    firstName,
    lastName,
    learnerEmail,
    phone,
    handleSubmit,
    navigateSignup,
    navigateConfirmation,
    navigateSelectSubject,
    productId,
    setEmailAssociatedWithAccount,
  } = props;

  const collectPhone =
    getDcatEoiVariant(productId) === 'variantB' ||
    getDhubEoiVariant(productId) === 'variantB' ||
    getVariantHubEoi(productId) === 'variantB';
  const isLoggedOut = !isAuthenticatedUser();

  const phoneNumberRef = useRef<HTMLInputElement>(null);
  const [hasFormError, setHasFormError] = useState<boolean>(false);
  const [firstNameError, setFirstNameError] = useState<string>('');
  const [lastNameError, setLastNameError] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [submitError, setSubmitError] = useState<string>('');

  const [emailAccountAssociation] = useMutation<EmailAccountAssociationResponse, EmailAccountAssociationVariables>(
    EMAIL_ACCOUNT_ASSOCIATION
  );
  const getEmailAssociation = async (): Promise<boolean> => {
    try {
      const emailAccountAssocationData = await emailAccountAssociation({
        variables: { id: learnerEmail, input: {} },
      });
      const isEmailAssociated =
        emailAccountAssocationData?.data?.wesCheckoutsResponse.elements[0].action === 'LOG_IN_TO_EXISTING_ACCOUNT';
      return new Promise((resolve) => resolve(isEmailAssociated));
    } catch (error) {
      Sentry.captureException(error);
      return new Promise((resolve) => resolve(false));
    }
  };

  const track = useTracker();

  // Field validations
  useEffect(() => {
    const error = firstNameErrorCheck(firstName);
    if (error) {
      setFirstNameError(error);
    } else {
      setFirstNameError('');
    }
  }, [firstName]);

  useEffect(() => {
    const error = lastNameErrorCheck(lastName);
    if (error) {
      setLastNameError(error);
    } else {
      setLastNameError('');
    }
  }, [lastName]);

  useEffect(() => {
    const error = isEmailValid(learnerEmail);
    if (error) {
      setEmailError(error);
    } else {
      setEmailError('');
    }
  }, [learnerEmail]);

  const onNext = () => {
    const fullName = `${firstName?.trim()} ${lastName?.trim()}`;
    const fullNameError = fullNameErrorCheck(fullName);

    if (firstNameError || lastNameError || emailError) {
      setHasFormError(true);
      track('submit_eoi', {
        eoiStatus: 'invalid_submission',
        eoiSection: 'initial_section',
        pageSection: {
          sectionName: 'eoi',
          subindex: 1,
        },
      });
    } else if (fullNameError) {
      setSubmitError(fullNameError);
      setHasFormError(true);
      track('submit_eoi', {
        eoiStatus: 'invalid_submission',
        eoiSection: 'initial_section',
        pageSection: {
          sectionName: 'eoi',
          subindex: 1,
        },
      });
    } else {
      handleSubmit()
        .then(() => {
          if (isLoggedOut) {
            getEmailAssociation().then((emailAssociatedWithAccount) => {
              if (emailAssociatedWithAccount) {
                setEmailAssociatedWithAccount(true);
                if (isDHubEoiOptEnabled(productId)) {
                  navigateSelectSubject();
                } else {
                  navigateConfirmation();
                }
              } else {
                navigateSignup();
              }
            });
          } else if (isDHubEoiOptEnabled(productId)) {
            navigateSelectSubject();
          } else {
            navigateConfirmation();
          }

          track('submit_eoi', {
            eoiStatus: 'success',
            eoiSection: 'initial_section',
            pageSection: {
              sectionName: 'eoi',
              subindex: 1,
            },
          });
        })
        .catch(() => {
          track('submit_eoi', {
            eoiStatus: 'api_failure',
            eoiSection: 'initial_section',
            pageSection: {
              sectionName: 'eoi',
              subindex: 1,
            },
          });

          setSubmitError(_t("Make sure all the information you've submitted is correct"));
        });
    }
  };

  // Some variables to support slight copy variations for logged in vs logged out users
  const headingSupportText =
    isLoggedOut && !isDHubEoiOptEnabled(productId)
      ? _t('All Coursera needs is your name and email. You can also continue with a Google or Facebook account.')
      : _t('All Coursera needs is your name and email.');

  const { HeadingGroup, Content, Actions } = Dialog;

  return (
    <>
      <HeadingGroup supportText={headingSupportText}>{_t('Getting started')}</HeadingGroup>
      <Content>
        {isLoggedOut && !isDHubEoiOptEnabled(productId) && (
          <>
            <EoiSSO />
            <Typography2
              component="p"
              variant="bodySecondary"
              color="supportText"
              align="center"
              css={styles.supportText}
            >
              <FormattedMessage
                message={_t('This site is protected by reCAPTCHA and the {privacyPolicyLink} and {tosLink} apply.')}
                privacyPolicyLink={
                  <Link href="https://policies.google.com/privacy">{_t('Google Privacy Policy')}</Link>
                }
                tosLink={<Link href="https://policies.google.com/terms">{_t('Terms of Service')}</Link>}
              />
            </Typography2>

            <Divider css={styles.divider} />
          </>
        )}

        {submitError && (
          <InlineNotification
            title={_t('There was an error with your submission')}
            severity="error"
            aria-live="assertive"
          >
            {submitError}
          </InlineNotification>
        )}

        <p aria-live="assertive" className="sr-only">
          {hasFormError && firstNameError}
          {hasFormError && lastNameError}
          {hasFormError && emailError}
        </p>

        <TextField
          label={_t('First Name')}
          fullWidth
          css={styles.textField}
          onChange={(e) => handleFirstNameChange(e.target.value)}
          validationStatus={hasFormError && !!firstNameError ? 'error' : undefined}
          validationLabel={firstNameError}
          value={firstName}
        />

        <TextField
          label={_t('Last Name')}
          fullWidth
          css={styles.textField}
          onChange={(e) => handleLastNameChange(e.target.value)}
          validationStatus={hasFormError && !!lastNameError ? 'error' : undefined}
          validationLabel={lastNameError}
          value={lastName}
        />

        <TextField
          label={_t('Email')}
          type="email"
          fullWidth
          css={styles.textField}
          onChange={(e) => handleEmailChange(e.target.value)}
          validationStatus={hasFormError && !!emailError ? 'error' : undefined}
          validationLabel={emailError}
          value={learnerEmail}
          autoComplete="email"
        />

        {collectPhone && (
          <InternationalPhoneNumberInput
            profile={{ phone }}
            customStyles={customFormattedPhoneStyles}
            handleFormChange={handlePhoneChange}
            handleError={(err) => {
              if (err) {
                setHasFormError(true);
              }
            }}
            ref={phoneNumberRef}
          />
        )}

        {collectPhone && (
          <Typography2 component="p" variant="bodySecondary" color="supportText" css={styles.supportText}>
            {_t(
              'By providing your phone number, you agree that Coursera may call or text you about educational opportunities.'
            )}
          </Typography2>
        )}

        <Typography2 component="p" variant="bodySecondary" color="supportText" css={styles.supportText}>
          {isLoggedOut &&
            _t(
              "By clicking 'Next' or continuing with your Google or Facebook Account, you agree that Coursera may contact you by email about educational opportunities. "
            )}

          {!isLoggedOut &&
            _t(
              "By clicking 'Next', you agree that Coursera may contact you by email about educational opportunities. "
            )}

          <FormattedMessage
            message={_t(
              'You can opt-out of communications at any time. Further information is available in {courseraPrivacyPolicy}.'
            )}
            courseraPrivacyPolicy={
              <Link href={`${config.url.base}about/privacy`}>{_t("Coursera's Privacy Notice")}</Link>
            }
          />
        </Typography2>
      </Content>
      <Actions>
        <Button onClick={onNext}>{_t('Next')}</Button>
      </Actions>
    </>
  );
};

export default InitialSection;
